import type { TemplateCreateOperation } from '@festina/configuration-general-types/configuration'
import { assertNotEmpty, notEmpty, type Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useLazyLayerMetaModel } from '../context/useLazyLayerMetaModel'
import { isModelMetaType } from '../types/metaModel'
import {
  ConfigurationFieldType,
  type TemplateField,
} from '../types/templateModel'
import { useOperationsContext } from '../workspace/operationsContext'
import {
  type EnrichedTemplateCreateOperationWithVersionInfo,
  isNotInherited,
} from './templateOperations/types'
import {
  toConfigurationExpressionValue,
  toServerTemplateFields,
  toServerTemplateWithoutFields,
} from './transformers/toServerModelConverter'
import {
  functionPath,
  generateFields,
} from './transformers/toVisualModelConverter'

export type TemplateFieldsProps = {
  enrichedTemplate: Optional<EnrichedTemplateCreateOperationWithVersionInfo>
}

export const useTemplateFields = ({
  enrichedTemplate,
}: TemplateFieldsProps) => {
  const { onAddOrUpdateOperation } = useOperationsContext()
  const metaModel = useLazyLayerMetaModel(
    enrichedTemplate?.versionInfo.layer.layerId,
  )

  return useMemo(() => {
    const template = enrichedTemplate?.entity
    const className = template?.templateTypeName

    const type = className ? metaModel.types.get(className) : undefined

    if (!template) {
      return []
    }

    const path = `{${template.name}}`

    const handleUpdateTemplateFields = async (nextFields: TemplateField[]) => {
      const nextValidField = nextFields.find((field) => field.name === 'valid')
      const nextFieldsWithoutValid = nextFields.filter(
        (field) => field !== nextValidField,
      )
      const nextTemplateProperties = toServerTemplateFields(
        nextFieldsWithoutValid,
      )
      const nextValidInOut =
        nextValidField?.value &&
        nextValidField.value.fieldType ===
          ConfigurationFieldType.ConfigurationExpressionValue
          ? toConfigurationExpressionValue(nextValidField.value)
          : undefined
      const {
        origin: _,
        versionInfo: __,
        ...creationWithoutOrigin
      } = enrichedTemplate
      const { origin: ___, ...templateWithoutOrigin } =
        creationWithoutOrigin.entity!

      const declaredFunctions = template.declaredFunctions
        ?.filter(
          (declaredFunction) =>
            declaredFunction?.function &&
            isNotInherited({
              origin: declaredFunction.origin,
              path: functionPath({
                basePath: path,
                source: declaredFunction.function,
              }),
            }),
        )
        .filter(notEmpty)
        .map((f) => {
          const { origin: _, documentation, ...declaredFunction } = f

          return {
            ...declaredFunction,
            ...(documentation?.value
              ? { documentation: documentation.value }
              : {}),
          }
        })

      const dynamicFunctions = template.dynamicFunctions
        ?.filter(
          (dynamicFunction) =>
            dynamicFunction?.function &&
            isNotInherited({
              origin: dynamicFunction.origin,
              path: functionPath({
                basePath: path,
                source: dynamicFunction.function,
              }),
            }),
        )
        .filter(notEmpty)
        .map((functionValue) => {
          const { origin: _, documentation, ...dynamicFunction } = functionValue

          return {
            ...dynamicFunction,
            ...(documentation?.value
              ? { documentation: documentation.value }
              : {}),
          }
        })

      assertNotEmpty(creationWithoutOrigin.entity?._type)

      const nextCreationOperation = {
        ...creationWithoutOrigin,
        entity: {
          _type: creationWithoutOrigin.entity._type,
          ...toServerTemplateWithoutFields(templateWithoutOrigin),
          valid: nextValidInOut,
          properties: nextTemplateProperties,
          ...(declaredFunctions?.length ? { declaredFunctions } : {}),
          ...(dynamicFunctions?.length ? { dynamicFunctions } : {}),
        },
      } satisfies TemplateCreateOperation

      await onAddOrUpdateOperation({
        operation: nextCreationOperation,
        layer: enrichedTemplate.versionInfo.layer,
        block: enrichedTemplate.versionInfo.block,
        // Irrelevant, you can only edit the fields of a draft, which can only exist in the workspace.
        shouldOpenUnifiedBranchWorkspace: false,
      })
    }

    return generateFields({
      template,
      path,
      meta: metaModel,
      templateType: isModelMetaType(type) ? type : undefined,
      updateField: handleUpdateTemplateFields,
      isParentNotRelevant: false,
    })
  }, [onAddOrUpdateOperation, enrichedTemplate, metaModel])
}
